import { useEffect, useState } from "react";
import { Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RemindersSection } from "@/components/RemindersSection";

function formatWaktu(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function Announcements() {
  const { user, schoolId } = useAuth();
  const canSend = ["super_admin", "bendahara"].includes(user.role);
  const [list, setList] = useState(null);
  const [classes, setClasses] = useState([]);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", target: "semua" });
  const [sending, setSending] = useState(false);

  const load = () => {
    setNeedsSchool(false);
    api.get("/announcements")
      .then((r) => setList(r.data))
      .catch((e) => {
        if (e.response?.status === 400) { setNeedsSchool(true); setList([]); }
        else { setList([]); toast.error(apiError(e)); }
      });
    api.get("/students")
      .then((r) => setClasses([...new Set(r.data.map((s) => s.class_name))].sort()))
      .catch(() => {});
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); setList([]); return; }
    load();
  }, [schoolId]);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    const [mode, cls] = form.target.startsWith("kelas|") ? ["kelas", form.target.slice(6)] : [form.target, ""];
    try {
      const r = await api.post("/announcements", {
        title: form.title,
        message: form.message,
        target_mode: mode,
        target_class: cls,
      });
      toast.success(`Pengumuman terkirim ke ${r.data.sent_count} wali${r.data.skipped_count ? ` (${r.data.skipped_count} siswa tanpa No WA)` : ""}`);
      setForm({ title: "", message: "", target: "semua" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="announcements-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Pemberitahuan</h1>
        <p className="mt-1 text-sm text-slate-500">Kirim pengumuman WhatsApp ke orang tua — semua siswa atau per kelas.</p>
      </div>

      {canSend && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-[#0060AF]" strokeWidth={1.5} />
            <h3 className="font-heading text-base font-semibold text-slate-800">Buat Pengumuman</h3>
          </div>
          <form onSubmit={handleSend} className="mt-4 space-y-4" data-testid="announcement-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ann-title">Judul Pengumuman</Label>
                <Input id="ann-title" data-testid="announcement-title-input" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Contoh: Libur Akhir Semester" />
              </div>
              <div className="space-y-2">
                <Label>Target</Label>
                <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                  <SelectTrigger data-testid="announcement-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semua" data-testid="announcement-target-semua">Semua Siswa</SelectItem>
                    {classes.map((c) => (
                      <SelectItem key={c} value={`kelas|${c}`} data-testid={`announcement-target-${c}`}>Kelas {c}</SelectItem>
                    ))}
                    <SelectItem value="spp" data-testid="announcement-target-spp">Belum Lunas SPP Bulan Ini</SelectItem>
                    <SelectItem value="tagihan" data-testid="announcement-target-tagihan">Belum Lunas Tagihan</SelectItem>
                  </SelectContent>
                </Select>
                {(form.target === "spp" || form.target === "tagihan") && (
                  <p className="text-xs text-amber-600" data-testid="announcement-tunggakan-note">Pesan otomatis ditambah rincian nama siswa + nominal tunggakan per wali.</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ann-message">Isi Pesan</Label>
              <Textarea id="ann-message" data-testid="announcement-message-input" value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={4}
                        placeholder="Tulis pengumuman untuk orang tua..." />
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-400">Terkirim ke nomor WA wali yang terdaftar di data siswa. Siswa tanpa No WA akan dilewati.</p>
              <Button type="submit" disabled={sending} data-testid="announcement-send-button"
                      className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
                <Send className="h-4 w-4" strokeWidth={1.5} /> {sending ? "Mengirim..." : "Kirim Pengumuman"}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-base font-semibold text-slate-800">Riwayat Pengumuman</h3>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="announcements-table">
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Judul</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="text-right">Terkirim</TableHead>
                <TableHead className="text-right">Tanpa WA</TableHead>
                <TableHead>Oleh</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list === null && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {list !== null && list.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Belum ada pengumuman.</TableCell></TableRow>
              )}
              {(list || []).map((a) => (
                <TableRow key={a.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`announcement-row-${a.id}`}>
                  <TableCell className="whitespace-nowrap text-sm text-slate-600">{formatWaktu(a.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{a.title}</TableCell>
                  <TableCell>
                    <Badge className="rounded-full bg-[#E5F0FA] text-[#0060AF] hover:bg-[#E5F0FA]">
                      {a.target_mode === "spp" ? "Belum Lunas SPP" : a.target_mode === "tagihan" ? "Belum Lunas Tagihan" : a.target_class ? `Kelas ${a.target_class}` : "Semua Siswa"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium text-emerald-600">{a.sent_count}</TableCell>
                  <TableCell className="text-right text-sm text-slate-500">{a.skipped_count}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <RemindersSection />
    </div>
  );
}
